import {AppState, Platform} from 'react-native';
import {getAnalytics, logEvent} from '@react-native-firebase/analytics';

export type DeepLinkSource = 'initial' | 'event' | 'push';

type NavigationKind = 'stack' | 'tab' | 'home';

export type ResolvedDeepLink = {
  isValid: boolean;
  url: string;
  source: DeepLinkSource;
  screen: string;
  routeName: string;
  navigationKind: NavigationKind;
  requiresAuth: boolean;
  params?: Record<string, unknown>;
  trackingParams: Record<string, string>;
  reason?: string;
};

type ParsedDeepLinkUrl = {
  path: string;
  segments: string[];
  queryParams: Record<string, string>;
};

const TRACKING_PARAM_PREFIXES = ['utm_'];
const TRACKING_PARAM_KEYS = ['gclid', 'fbclid', 'ad_id'];

const STACK_SCREEN_CONFIG: Record<
  string,
  {routeName: string; requiresAuth: boolean}
> = {
  Emprestimo: {routeName: 'Emprestimo', requiresAuth: true},
  Fipe: {routeName: 'Fipe', requiresAuth: true},
  Reembolsos: {routeName: 'Reembolsos', requiresAuth: true},
  SolicitaReembolso: {routeName: 'SolicitaReembolso', requiresAuth: true},
  PedidosReembolso: {routeName: 'PedidosReembolso', requiresAuth: true},
  Checkin: {routeName: 'Checkin', requiresAuth: true},
  Roulette: {routeName: 'Roulette', requiresAuth: true},
  Achievements: {routeName: 'Achievements', requiresAuth: true},
  BaratinhasAjuda: {routeName: 'BaratinhasAjuda', requiresAuth: false},
  ReferAndWin: {routeName: 'ReferAndWin', requiresAuth: false},
  CardsWallet: {routeName: 'CardsWallet', requiresAuth: true},
};

const TAB_SCREEN_CONFIG: Record<
  string,
  {routeName: string; requiresAuth: boolean}
> = {
  Shopping: {routeName: 'Shopping', requiresAuth: false},
  VehicleDebits: {routeName: 'VehicleDebits', requiresAuth: true},
  Coupons: {routeName: 'Coupons', requiresAuth: true},
};

function decode(value: string) {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

function parseQueryString(queryString: string) {
  const params: Record<string, string> = {};

  queryString
    .replace(/^\?/, '')
    .split('&')
    .filter(Boolean)
    .forEach(pair => {
      const [rawKey, ...rawValue] = pair.split('=');
      const key = decode(rawKey);
      if (!key) {
        return;
      }
      params[key] = decode(rawValue.join('='));
    });

  return params;
}

function parseDeepLinkUrl(url: string): ParsedDeepLinkUrl {
  const withoutHash = url.split('#')[0];
  const [urlWithoutQuery, rawQuery = ''] = withoutHash.split('?');
  const queryParams = parseQueryString(rawQuery);

  let path = '/';

  if (/^barataoapp:\/\//i.test(urlWithoutQuery)) {
    const schemePath = urlWithoutQuery.replace(/^barataoapp:\/\//i, '');
    path = `/${schemePath.replace(/^\/+/, '')}`;
  } else if (/^https?:\/\//i.test(urlWithoutQuery)) {
    const absolutePathMatch = urlWithoutQuery.match(
      /^https?:\/\/[^/]*(\/.*)?$/i,
    );
    path = absolutePathMatch?.[1] || '/';
  } else {
    path = urlWithoutQuery.startsWith('/')
      ? urlWithoutQuery
      : `/${urlWithoutQuery}`;
  }

  const normalizedPath = `/${path.replace(/^\/+/, '').replace(/\/+$/, '')}`;
  const segments =
    normalizedPath === '/'
      ? []
      : normalizedPath
          .split('/')
          .filter(Boolean)
          .map(segment => decode(segment));

  return {
    path: normalizedPath,
    segments,
    queryParams,
  };
}

function getTrackingParams(queryParams: Record<string, string>) {
  return Object.entries(queryParams).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (
        TRACKING_PARAM_KEYS.includes(key) ||
        TRACKING_PARAM_PREFIXES.some(prefix => key.startsWith(prefix))
      ) {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );
}

function homeResolution(
  url: string,
  source: DeepLinkSource,
  trackingParams: Record<string, string>,
  reason?: string,
): ResolvedDeepLink {
  return {
    isValid: true,
    url,
    source,
    screen: 'Home',
    routeName: 'Home',
    navigationKind: 'home',
    requiresAuth: false,
    trackingParams,
    reason,
  };
}

function invalidResolution(
  url: string,
  source: DeepLinkSource,
  trackingParams: Record<string, string>,
  reason: string,
): ResolvedDeepLink {
  return {
    isValid: false,
    url,
    source,
    screen: 'Home',
    routeName: 'Home',
    navigationKind: 'home',
    requiresAuth: false,
    trackingParams,
    reason,
  };
}

function resolveDirectScreen(
  screen: string,
  url: string,
  source: DeepLinkSource,
  trackingParams: Record<string, string>,
): ResolvedDeepLink | null {
  const tabConfig = TAB_SCREEN_CONFIG[screen];
  if (tabConfig) {
    return {
      isValid: true,
      url,
      source,
      screen,
      routeName: tabConfig.routeName,
      navigationKind: 'tab',
      requiresAuth: tabConfig.requiresAuth,
      trackingParams,
    };
  }

  const stackConfig = STACK_SCREEN_CONFIG[screen];
  if (stackConfig) {
    return {
      isValid: true,
      url,
      source,
      screen,
      routeName: stackConfig.routeName,
      navigationKind: 'stack',
      requiresAuth: stackConfig.requiresAuth,
      trackingParams,
    };
  }

  return null;
}

function resolveStationsScreen(
  url: string,
  source: DeepLinkSource,
  trackingParams: Record<string, string>,
  tipo?: string,
  cidade?: string,
): ResolvedDeepLink {
  if (!tipo) {
    return invalidResolution(
      url,
      source,
      trackingParams,
      'missing_stations_tipo',
    );
  }

  if (cidade) {
    return {
      isValid: true,
      url,
      source,
      screen: 'Stations',
      routeName: 'ListagemPostos',
      navigationKind: 'stack',
      requiresAuth: false,
      params: {
        tipo,
        cidade,
        isUserLocation: false,
      },
      trackingParams,
    };
  }

  return {
    isValid: true,
    url,
    source,
    screen: 'Stations',
    routeName: 'Localization',
    navigationKind: 'stack',
    requiresAuth: false,
    params: {
      tipo,
    },
    trackingParams,
  };
}

function resolveShoppingScreen(
  url: string,
  source: DeepLinkSource,
  trackingParams: Record<string, string>,
  tipo?: string,
  cidade?: string,
): ResolvedDeepLink {
  if (!tipo) {
    const resolved = resolveDirectScreen(
      'Shopping',
      url,
      source,
      trackingParams,
    );

    if (resolved) {
      return resolved;
    }
  }

  const normalizedTipo = tipo?.toUpperCase();

  if (normalizedTipo === 'M') {
    const resolved = resolveDirectScreen(
      'VehicleDebits',
      url,
      source,
      trackingParams,
    );

    if (resolved) {
      return {
        ...resolved,
        screen: 'Shopping',
        reason: 'shopping_category_vehicle_debits',
      };
    }
  }

  if (normalizedTipo === 'B') {
    return {
      isValid: true,
      url,
      source,
      screen: 'Shopping',
      routeName: 'Shopping',
      navigationKind: 'tab',
      requiresAuth: true,
      params: {
        tipo: normalizedTipo,
      },
      trackingParams,
      reason: 'shopping_category_boleto_fallback',
    };
  }

  return resolveStationsScreen(
    url,
    source,
    trackingParams,
    normalizedTipo,
    cidade,
  );
}

function resolveOpenAlias(
  url: string,
  source: DeepLinkSource,
  trackingParams: Record<string, string>,
  queryParams: Record<string, string>,
): ResolvedDeepLink {
  const screen = queryParams.screen;

  if (!screen) {
    return invalidResolution(url, source, trackingParams, 'missing_screen');
  }

  if (screen === 'Stations') {
    return resolveStationsScreen(
      url,
      source,
      trackingParams,
      queryParams.tipo,
      queryParams.cidade,
    );
  }

  if (screen === 'Shopping') {
    return resolveShoppingScreen(
      url,
      source,
      trackingParams,
      queryParams.tipo,
      queryParams.cidade,
    );
  }

  const resolved = resolveDirectScreen(screen, url, source, trackingParams);
  if (resolved) {
    return resolved;
  }

  return invalidResolution(url, source, trackingParams, 'unknown_screen');
}

export function resolveDeepLink(
  url: string,
  source: DeepLinkSource,
): ResolvedDeepLink {
  const parsed = parseDeepLinkUrl(url);
  const trackingParams = getTrackingParams(parsed.queryParams);
  const [firstSegment, secondSegment, thirdSegment] = parsed.segments;

  if (!firstSegment) {
    return invalidResolution(
      url,
      source,
      trackingParams,
      'root_screen_param_disabled',
    );
  }

  if (firstSegment === 'install') {
    return homeResolution(url, source, trackingParams, 'legacy_install');
  }

  if (firstSegment === 'open') {
    return resolveOpenAlias(url, source, trackingParams, parsed.queryParams);
  }

  if (firstSegment === 'Stations') {
    return resolveStationsScreen(
      url,
      source,
      trackingParams,
      secondSegment,
      thirdSegment,
    );
  }

  if (firstSegment === 'Shopping') {
    return resolveShoppingScreen(
      url,
      source,
      trackingParams,
      secondSegment,
      thirdSegment,
    );
  }

  const resolved = resolveDirectScreen(
    firstSegment,
    url,
    source,
    trackingParams,
  );
  if (resolved) {
    return resolved;
  }

  return invalidResolution(url, source, trackingParams, 'unknown_path');
}

export async function logDeepLinkEvent(
  eventName:
    | 'deep_link_open'
    | 'deep_link_resolved'
    | 'deep_link_requires_auth'
    | 'deep_link_invalid',
  resolved: ResolvedDeepLink,
) {
  if (Platform.OS === 'web') {
    return;
  }

  try {
    const analytics = getAnalytics();
    await logEvent(analytics, eventName, {
      url: resolved.url.slice(0, 500),
      source: resolved.source,
      screen: resolved.screen,
      route_name: resolved.routeName,
      is_valid: resolved.isValid ? 'true' : 'false',
      requires_auth: resolved.requiresAuth ? 'true' : 'false',
      reason: resolved.reason ?? '',
      app_state: AppState.currentState,
      ...resolved.trackingParams,
    });
  } catch (error) {
    console.error('[DeepLink] Error logging event:', error);
  }
}
